// eslint-disable-next-line no-restricted-syntax
import { createIntl, createIntlCache } from 'react-intl';
import { isEmpty, mapValues, identity, noop } from 'lodash';
import { buildFormattedMessageMap } from 'modules/taboola-common-frontend-modules/i18n/components/FormattedMessage/buildFormattedMessageMap';
import { defaultLocale } from 'modules/taboola-common-frontend-modules/i18n/config/messages';
import { getMessagesCache } from 'modules/taboola-common-frontend-modules/i18n/services/messagesCache';
import { valueStoreFactory } from './utils/valueStoreFactory';

const registryStore = valueStoreFactory();
registryStore.setValue(new WeakMap());

const getMessageData = element => {
    if (!element) {
        return {};
    }

    return registryStore.getValue().get(element) || {};
};

const traverseForMessageData = (element, textContentOnly = false) => {
    const englishTexts = [];
    const messageIds = [];

    if (!element) {
        return { englishTexts, messageIds };
    }

    const stack = [element];

    while (stack.length > 0) {
        const el = stack.pop();
        const messageData = getMessageData(el);
        const elementText = textContentOnly ? el.textContent?.trim() : el.innerText?.trim();
        const children = el.children;

        if (messageData && messageData.englishText) {
            englishTexts.push(messageData.englishText);
            messageIds.push(messageData.id);
            continue;
        }

        // Fallback if no more children but untranslated text is found, return text as is
        if (!isEmpty(elementText) && isEmpty(children)) {
            englishTexts.push(elementText);
            continue;
        }

        // Add children to stack (in reverse order to maintain original traversal order)
        for (let i = children?.length - 1; i >= 0; i--) {
            stack.push(children[i]);
        }
    }

    return { englishTexts, messageIds };
};

const getIntl = (() => {
    let cachedIntl = null;
    let messagesCache = getMessagesCache();

    return () => {
        const fetchedMessages = getMessagesCache();
        const englishMessagesCache = messagesCache?.[defaultLocale];
        const fetchedEnglishMessages = fetchedMessages?.[defaultLocale];

        if (!fetchedEnglishMessages) {
            return null;
        }

        if (!cachedIntl || englishMessagesCache !== fetchedEnglishMessages) {
            cachedIntl = createIntl(
                {
                    locale: defaultLocale,
                    messages: fetchedEnglishMessages,
                    onError: noop,
                },
                createIntlCache()
            );
            messagesCache = fetchedMessages;
        }

        return cachedIntl;
    };
})();

const componentMappedValues = buildFormattedMessageMap({}, '');

const getEnglishText = (messageId, mappedValues = {}) => {
    const intl = getIntl();

    if (!intl) {
        return null;
    }

    const filteredMappedValues = mapValues(mappedValues, (value, key) => {
        if (componentMappedValues[key]) {
            return identity;
        }

        return value;
    });

    return intl.formatMessage({ id: messageId }, filteredMappedValues);
};

export const registerElement = (element, messageId, mappedValues) => {
    if (!element || !messageId) {
        return;
    }

    const englishText = getEnglishText(messageId, mappedValues);
    if (englishText) {
        const messageData = {
            id: messageId,
            englishText,
        };
        registryStore.getValue().set(element, messageData);
    }
};

export const collectAllMessageTextsInElement = (element, textContentOnly = false) => {
    if (!element) {
        return { englishText: '', messageIds: [] };
    }

    const { englishTexts, messageIds } = traverseForMessageData(element, textContentOnly);

    return {
        englishText: englishTexts.join(' '),
        messageIds,
    };
};
